import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function StudentDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<{ name?: string; email?: string; role?: string } | null>(null);

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (!userStr || !token) {
        navigate('/login', { replace: true });
        return;
      }
      const parsedUser = JSON.parse(userStr);
      if (parsedUser.role !== 'student') {
        navigate('/login', { replace: true });
        return;
      }
      setUser(parsedUser);
    } catch (e) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/', { replace: true });
  };

  const initials = (name?: string) => {
    if (!name) return 'S';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-6 z-10 sticky top-0">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="GCSE Computing Logo" className="h-9 w-auto object-contain" />
          <span className="font-bold text-lg text-slate-900 tracking-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
            GCSE <span className="text-emerald-700">Computing</span>
          </span>
        </Link>
        <div className="flex items-center gap-4">
          <div className="text-right hidden md:block">
            <p className="text-sm font-semibold text-slate-800">{user?.name}</p>
            <p className="text-xs text-slate-500">Student</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">
            {initials(user?.name)}
          </div>
          <button
            onClick={handleLogout}
            className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
            title="Logout"
          >
            <i className="ri-logout-box-r-line text-xl"></i>
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-slate-200 hidden md:flex flex-col h-[calc(100vh-4rem)] sticky top-16">
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Main Menu</div>

            <a href="#" className="flex items-center gap-3 px-3 py-2.5 bg-emerald-50 text-emerald-700 rounded-lg font-semibold text-sm">
              <i className="ri-dashboard-line text-lg"></i>
              Overview
            </a>
            <Link to="/gcse/topics" className="flex items-center gap-3 px-3 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-emerald-600 rounded-lg font-medium text-sm transition-colors">
              <i className="ri-book-open-line text-lg"></i>
              Syllabus & Topics
            </Link>
            <Link to="/gcse/past-papers" className="flex items-center gap-3 px-3 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-emerald-600 rounded-lg font-medium text-sm transition-colors">
              <i className="ri-file-paper-2-line text-lg"></i>
              Past Papers
            </Link>
            <Link to="/gcse/mock-exams" className="flex items-center gap-3 px-3 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-emerald-600 rounded-lg font-medium text-sm transition-colors">
              <i className="ri-edit-box-line text-lg"></i>
              Mock Exams
            </Link>
            <Link to="/events" className="flex items-center gap-3 px-3 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-emerald-600 rounded-lg font-medium text-sm transition-colors">
              <i className="ri-calendar-event-line text-lg"></i>
              Live Sessions
            </Link>

            <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider mt-6 mb-2">Account</div>
            <Link to="/contact" className="flex items-center gap-3 px-3 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-emerald-600 rounded-lg font-medium text-sm transition-colors">
              <i className="ri-message-3-line text-lg"></i>
              Contact Support
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-rose-500 hover:bg-rose-50 rounded-lg font-medium text-sm transition-colors"
            >
              <i className="ri-logout-box-r-line text-lg"></i>
              Logout
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 overflow-auto h-[calc(100vh-4rem)]">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
              Welcome back, {user?.name?.split(' ')[0] || 'Student'}! 👋
            </h1>
            <p className="text-slate-500 mb-8 text-sm">Here is your revision progress and upcoming activities.</p>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
              {[
                { label: 'Topics Completed', value: '12', icon: 'ri-book-read-line', color: 'bg-blue-50 text-blue-600' },
                { label: 'Mock Exams Taken', value: '4', icon: 'ri-medal-line', color: 'bg-amber-50 text-amber-600' },
                { label: 'Study Time Logged', value: '28h', icon: 'ri-time-line', color: 'bg-emerald-50 text-emerald-600' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                  <div className={`w-11 h-11 ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <p className="text-3xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>{stat.value}</p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Continue Learning */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-sm">
                  <i className="ri-play-circle-line text-emerald-600 text-base"></i> Continue Learning
                </h3>
                <div className="space-y-4">
                  {[
                    { title: 'Data Representation', sub: 'Binary, Hexadecimal, Character Sets', pct: 65 },
                    { title: 'Python Programming', sub: 'Loops, Variables, and Functions', pct: 30 },
                    { title: 'Computer Networks', sub: 'TCP/IP, HTTP, and DNS Protocols', pct: 10 },
                  ].map((item) => (
                    <div key={item.title} className="p-4 border border-slate-100 rounded-lg hover:border-emerald-200 hover:shadow-sm transition-all cursor-pointer group">
                      <div className="flex justify-between items-start mb-1.5">
                        <p className="font-semibold text-slate-800 text-sm group-hover:text-emerald-700 transition-colors">{item.title}</p>
                        <span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md ml-2 flex-shrink-0">{item.pct}%</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-2.5">{item.sub}</p>
                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div className="bg-emerald-600 h-1.5 rounded-full transition-all" style={{ width: `${item.pct}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/gcse/topics" className="block w-full mt-4 py-2.5 text-sm font-semibold text-center text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors border border-emerald-200">
                  View All Topics →
                </Link>
              </div>

              {/* Upcoming Sessions */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-sm">
                  <i className="ri-calendar-event-line text-emerald-600 text-base"></i> Upcoming Live Sessions
                </h3>
                <div className="space-y-3">
                  {[
                    { day: '15', month: 'Aug', title: 'Algorithms Masterclass', time: '18:00 – 19:30', tutor: 'Sarah M.', dark: false },
                    { day: '22', month: 'Aug', title: 'Exam Technique Workshop', time: '17:00 – 18:30', tutor: 'Dr. Smith', dark: true },
                    { day: '29', month: 'Aug', title: 'Python Problem Solving', time: '18:00 – 19:00', tutor: 'Sarah M.', dark: false },
                  ].map((s) => (
                    <div key={s.day + s.month} className="flex items-start gap-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div className={`w-12 h-12 flex-shrink-0 flex flex-col items-center justify-center rounded-lg text-white ${s.dark ? 'bg-slate-800' : 'bg-emerald-700'}`}>
                        <span className="text-sm font-bold leading-none">{s.day}</span>
                        <span className="text-xs opacity-80">{s.month}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">{s.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{s.time} · with {s.tutor}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/events" className="block w-full mt-4 py-2.5 text-sm font-semibold text-center text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors border border-emerald-200">
                  View All Sessions →
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
